/**
 * フリーOCRで「名前が無い項目・かたまり」を判定する（#3249）
 *
 * 文書には項目名を持たない文字列（表題・ロゴ・注記・敬称・本文など）が書かれている。
 * これらを項目名の位置に置くと「項目名の欄に本文が入り、値が空」という表示になるため、
 * AIには `_1`, `_2` … という連番キーに値として格納させ、表示時は項目名の欄を空にする。
 *
 * かたまり（セクション）も同様に、文書に見出しが印字されていないものがある。
 * まとまりとしての構造は残したいが、AIが「発行元」のような名前を考えて付けると
 * 文書に無い情報を足すことになる。そのため名前のないかたまりは `_g1`, `_g2` …
 * という無意味な連番キーでネストさせ、表示では見出しを空にしてインデントだけ残す。
 *
 * 判定は連番キーの形式で行う。プロンプトでは併せて no_label フラグも指示しているが、
 * responseSchema を持たないJSON modeではフラグの付与が安定しないため、
 * キー名を主たる判定条件とし、フラグは補助として見る。
 */

/** `_1`, `_2` … の形式のキー（項目名が無い行に割り当てられる連番キー） */
const NO_LABEL_KEY_PATTERN = /^_\d+$/;

/** `_g1`, `_g2` … の形式のキー（見出しが無いかたまりに割り当てられる連番キー） */
const NO_LABEL_GROUP_KEY_PATTERN = /^_g\d+$/;

export const isNoLabelKey = (key: string): boolean =>
  NO_LABEL_KEY_PATTERN.test(key) || NO_LABEL_GROUP_KEY_PATTERN.test(key);

/**
 * 項目名（見出し）の欄に表示すべきラベルを返す。
 * 名前が無い項目・かたまりでは空文字列を返す。
 */
export const resolveDisplayLabel = (key: string, value: unknown): string => {
  if (isNoLabelKey(key)) return '';
  if (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    (value as Record<string, unknown>).no_label === true
  ) {
    return '';
  }
  return key;
};

/**
 * 「文書に項目名が無い行」か（#3315）。
 *
 * 項目名の入力欄を出すかどうかの判定に使う。resolveDisplayLabel が空文字を返すかで
 * 判定すると、ユーザーが項目名を全消しして空文字になったキーまで「項目名が無い行」と
 * みなされ、入力欄が消えて直せなくなる。
 * 入力欄の出し分けは、連番キー・no_label フラグだけで判定する。
 */
export const isNoLabelEntry = (key: string, value: unknown): boolean => {
  if (isNoLabelKey(key)) return true;
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    (value as Record<string, unknown>).no_label === true
  );
};
